import {useState, useEffect} from 'react';

import backendCommunicator from 'services/backendCommunicator';
import Alert from 'helpers/Alert';
import {methodType, FormSelectionEnum, inputMenuActionType, SWRKeyEnum} from 'interfaces';

import useParseURL from 'hooks/useParseURL';

import {useInputMenuContext} from 'context/InputMenu';

import {type ConcessionMAB, type Concession} from 'client';

import {type TreeSelection} from 'components/InputMenu/Concession/AssignTree';
import useIsMounted from 'hooks/useIsMounted';
import {mutate} from 'swr';
import {useGetCompanies, useGetPlanSettings, useGetRegions} from 'hooks/swr';

export declare type UpdateConcessionForm = Omit<Concession, 'mab'> & {mab?: Array<Partial<ConcessionMAB>>};

const initialState = (planId: string): UpdateConcessionForm => {
  return {
    fishTypeIds: [] as string[],
    name: '',
    comment: '',
    cageIds: [] as string[],
    siteIds: [] as string[],
    priority: 0,
    planId,
    wholeCageAssignment: true,
    assignToAllFishTypes: true,
    assignToAllCages: true,
    id: '',
    regionId: '',
    companyId: '',
    requiredFishGroupPropertyIds: [],
  };
};

const assignTreeInitialState = {
  [FormSelectionEnum.plan]: [],
  [FormSelectionEnum.site]: [],
  [FormSelectionEnum.cage]: [],
};

function useAddEditConcession() {
  const {planId} = useParseURL();
  const isMounted = useIsMounted();

  const [concession, setConcession] = useState<UpdateConcessionForm>(initialState(planId));
  const [selectedSitesCages, setSelectedSitesCages] = useState<TreeSelection>(assignTreeInitialState);
  const {data: regions} = useGetRegions({resourceId: planId});
  const {data: companies} = useGetCompanies({resourceId: planId});
  const {data: setting} = useGetPlanSettings({resourceId: planId});

  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const concessionId = inputMenuState?.formData?.id;

  const [createUpdateConcession, setCreateUpdateConcession] = useState<boolean>(false);

  useEffect(() => {
    if (companies && (concession.companyId === '' || concession.regionId === '') && companies.length > 0) {
      const defaultCompany = companies.find((company) => company.isDefault);
      const regionId =
        regions !== undefined && regions[0] && concession?.regionId === '' && setting?.hideRegions ? regions[0].id : '';
      if (defaultCompany) {
        if (concession.companyId !== defaultCompany.id || concession.regionId !== regionId) {
          setConcession({...concession, companyId: defaultCompany.id, regionId});
        }
      }
    }
  }, [regions, setting?.hideRegions, companies, concession]);

  const communicationSuccess = () => {
    if (isMounted.current) {
      setCreateUpdateConcession(false);
      setConcession(initialState(planId));
      void mutate([planId, SWRKeyEnum.GET_PLAN_CONCESSIONS]);
      void mutate([planId, SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
      inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
    }
  };
  useEffect(() => {
    if (inputMenuState?.method === methodType.edit && concessionId) {
      backendCommunicator
        .getConcession(concessionId)
        .then((response) => {
          const concessionToUpdate = response.data;
          concessionToUpdate.mab?.sort((a, b) => (a.fromDate > b.fromDate ? 1 : -1));
          setConcession({
            ...concessionToUpdate,
          });
          concessionToUpdate.cageIds &&
            setSelectedSitesCages({
              ...selectedSitesCages,
              [FormSelectionEnum.site]: concessionToUpdate.siteIds,
              [FormSelectionEnum.cage]: concessionToUpdate.cageIds,
            });
        })
        .catch((error) => {
          Alert.error(error);
        });
    }
  }, [concessionId]);

  useEffect(() => {
    if (createUpdateConcession) {
      const request =
        concessionId && inputMenuState?.method === methodType.edit
          ? backendCommunicator.updateConcessions(concessionId, concession as Concession)
          : backendCommunicator.addConcessions(concession as Concession);

      request
        .then(() => {
          communicationSuccess();
        })
        .catch((error) => {
          if (isMounted.current) {
            Alert.error(error);
            setCreateUpdateConcession(false);
          }
        });
    }
  }, [createUpdateConcession]);

  return {
    concession,
    setConcession,
    selectedSitesCages,
    setSelectedSitesCages,
    setCreateUpdateConcession,
    isUpdating: createUpdateConcession,
  };
}

export default useAddEditConcession;
